import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Radio,
  Select,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AppDispatch, RootState } from '../../Redux/Store';
import {
  checkoutPublicReservation,
  fetchPublicAvailability,
  fetchPublicBusinessCalendar,
  finalizePublicReservation,
  fetchReservationForm,
  submitPublicReservation,
} from '../../Redux/PublicReservation/publicReservationApi';
import {
  clearCheckoutError,
  clearSubmitError,
  resetCheckout,
  resetPublicReservation,
} from '../../Redux/PublicReservation/publicReservationSlice';
import {
  BusinessCalendarDay,
  calcPriceIncludingTax,
  DEFAULT_TAX_RATE_PERCENT,
  PublicInquiryView,
  PublicMenuView,
  RefundTier,
  ReservationInput,
  toDateString,
} from '../../types/reservation';
import ReservationPaymentStep from './ReservationPaymentStep';
import {
  makeT,
  PublicReservationDictKey,
  PublicReservationLang,
} from '../../i18n/publicReservationI18n';
import PublicHeader from '../../Components/Header/PublicHeader';
import PublicFooter from '../../Components/Footer/PublicFooter';
import {
  AnonymizationNotice,
  FEAST_USER_PRIVACY_URL,
  LanguageToggle,
  PublicReservationFooter,
  formatMonthDay,
  formatPublicDateTime,
  formatYearMonth,
  googleMapsUrl,
  parseApiErrorMessage,
} from './shared';
import {
  isValidEmail,
  isValidPhoneNumber,
  normalizeEmail,
  normalizePhoneNumber,
} from '../../utils/contactValidation';

/**
 * 公開予約ページ（認証不要・/reservations/:store_id）。
 * 日時 → お客様情報 → 確認 → 完了 の 4 ステップで予約を作成する。
 * isPreview=true ではスタッフ向けプレビューとして描画し、送信は行わない（MenuPreview と同型・ADR 0030）。
 * 表示文言はすべて publicReservationI18n の辞書経由で ja/en を切り替える（ADR 0040）。
 * 同意チェックは店舗の「個人情報の取り扱い」への同意（URL 型=外部リンク /
 * TEXT 型=/reservations/:store_id/privacy）。フィードバック3。
 */

/** BE が「無制限」を表す際に使う Int.MAX_VALUE */
const INT_MAX = 2147483647;
/** 残枠がこの値以下のとき「残◯組」を表示する */
const REMAINING_BADGE_THRESHOLD = 3;
/** 公開フォームの人数上限（BE が maxPartySize を返さない場合のフォールバック） */
const DEFAULT_MAX_PARTY_SIZE = 12;
/** [min, max] の連続した人数配列を作る（min<1 や max<min は安全側に補正） */
const buildPartySizes = (min: number, max: number): number[] => {
  const lo = Math.max(1, min);
  const hi = Math.max(lo, max);
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
};

/**
 * ステップ定義。Phase 2 で決済ステップ（confirm と done の間）を差し込めるよう配列で管理し、
 * 画面側は key で分岐する。決済必須店舗のときだけ 'payment' を含める（動的挿入）。
 */
type StepKey = 'dateTime' | 'info' | 'confirm' | 'payment' | 'done';
interface StepDef {
  key: StepKey;
  labelKey: PublicReservationDictKey;
}
const BASE_STEP_DEFS: StepDef[] = [
  { key: 'dateTime', labelKey: 'stepDateTime' },
  { key: 'info', labelKey: 'stepInfo' },
  { key: 'confirm', labelKey: 'stepConfirm' },
  { key: 'done', labelKey: 'stepDone' },
];
const PAYMENT_STEP_DEF: StepDef = { key: 'payment', labelKey: 'stepPayment' };
/** confirm と done の間に決済ステップを挿入したステップ配列を作る */
const buildStepDefs = (withPayment: boolean): StepDef[] => {
  if (!withPayment) return BASE_STEP_DEFS;
  const confirmIdx = BASE_STEP_DEFS.findIndex((s) => s.key === 'confirm');
  return [
    ...BASE_STEP_DEFS.slice(0, confirmIdx + 1),
    PAYMENT_STEP_DEF,
    ...BASE_STEP_DEFS.slice(confirmIdx + 1),
  ];
};

/** 確認ステップのサマリ 1 行 */
const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'pre-wrap' }}>
      {value}
    </Typography>
  </Box>
);

interface PublicReservationProps {
  isPreview?: boolean;
}

const PublicReservation: React.FC<PublicReservationProps> = ({ isPreview = false }) => {
  const { store_id: storeId } = useParams<{ store_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const {
    form,
    formLoading,
    formError,
    availability,
    availabilityLoading,
    businessCalendar,
    calendarLoading,
    submitting,
    submitError,
    result,
    checkout,
    checkingOut,
    checkoutError,
    finalizeResult,
    finalizing,
    finalizeError,
  } = useSelector((state: RootState) => state.publicReservation);

  const [lang, setLang] = useState<PublicReservationLang>('ja');
  const t = useMemo(() => makeT(lang), [lang]);

  // 決済必須店舗か（Phase 2）。paymentEnabled && paymentRequired のときだけ決済ステップを挿む。
  // isPreview では決済ステップを出さない（送信自体しないため）。
  const paymentRequired =
    !isPreview &&
    form?.payment?.paymentEnabled === true &&
    form?.payment?.paymentRequired === true;

  // 決済ステップを含めた実効ステップ配列と key→index ヘルパー。
  const stepDefs = useMemo(() => buildStepDefs(!!paymentRequired), [paymentRequired]);
  const stepIndex = useMemo(
    () => (key: StepKey): number => {
      const idx = stepDefs.findIndex((s) => s.key === key);
      return idx >= 0 ? idx : 0;
    },
    [stepDefs],
  );

  const [activeStep, setActiveStep] = useState(0);
  // デポジット・キャンセルポリシーへの同意（決済必須店舗の確認ステップで必須）
  const [depositAgreed, setDepositAgreed] = useState(false);
  // finalize を数回リトライしても未完了だった旨のフラグ（照会リンク誘導に使う）
  const [finalizePolling, setFinalizePolling] = useState(false);
  const [finalizeExhausted, setFinalizeExhausted] = useState(false);
  // 3DS リダイレクト復帰を1回だけ処理するためのガード
  const redirectHandled = useRef(false);

  // ---- Step1: 日時 ----
  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => toDateString(today), [today]);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1); // 1-12
  const [partySize, setPartySize] = useState(2);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // ---- Step2: お客様情報 ----
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedMenuId, setSelectedMenuId] = useState('');
  // 動的質問への回答。BOOLEAN は 'true' / 'false' で保持し、送信時に「はい」「いいえ」へ変換する
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [agreed, setAgreed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [previewSnackbar, setPreviewSnackbar] = useState(false);

  // 画像ビューア（コース画像・ギャラリーの全枚数表示ダイアログ）。null = 非表示
  const [viewerImages, setViewerImages] = useState<string[] | null>(null);

  // undefined は true 扱い（acceptingReservations 未対応の旧 BE レスポンス互換）
  const accepting = form ? form.acceptingReservations !== false : true;

  // 公開フォームで選べる人数の範囲（統合契約4）。
  // 席の連結を含む最大人数を BE が maxPartySize として返すため、それに合わせて選択肢を絞る。
  const partySizes = useMemo(
    () =>
      buildPartySizes(
        form?.minPartySize ?? 1,
        form?.maxPartySize ?? DEFAULT_MAX_PARTY_SIZE,
      ),
    [form?.minPartySize, form?.maxPartySize],
  );

  // 人数の範囲外（例: 上限が下がった）になったら範囲内に丸める
  useEffect(() => {
    if (partySizes.length === 0) return;
    const lo = partySizes[0];
    const hi = partySizes[partySizes.length - 1];
    if (partySize < lo) setPartySize(lo);
    else if (partySize > hi) setPartySize(hi);
  }, [partySizes, partySize]);
  const phoneRequired = form?.phoneRequired === true;

  // 税込表示に使う税率（旧 BE 未返却時は既定10%。フィードバック4）
  const taxRatePercent = form?.taxRatePercent ?? DEFAULT_TAX_RATE_PERCENT;
  /** コースの税込額（priceIncludingTax があればそれ、無ければ price から算出） */
  const menuTaxIncluded = (menu: PublicMenuView): number =>
    menu.priceIncludingTax ?? calcPriceIncludingTax(menu.price, taxRatePercent);
  /** コースの税込価格を「¥1,100（税込）」形式で表示する */
  const menuPriceLabel = (menu: PublicMenuView): string =>
    `¥${menuTaxIncluded(menu).toLocaleString()}${t('taxIncluded')}`;
  // コース選択必須（登録コースが1件以上ある場合のみ有効。0件なら設定に関わらず必須化しない）
  const courseRequired =
    form?.courseSelectionRequired === true && (form?.menus.length ?? 0) > 0;

  /**
   * 承認要否の告知（Wave A・5）。approvalMode で出し分け（未指定の旧BEでは出さない）。
   * MANUAL=店舗承認後に確定 / AUTO=このまま確定。トップ（日時ステップ）と確認ステップの
   * 目立つ位置に表示する。
   */
  const approvalNotice = (): React.ReactElement | null => {
    if (form?.approvalMode === 'MANUAL') {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('approvalManualNotice')}
        </Alert>
      );
    }
    if (form?.approvalMode === 'AUTO') {
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('approvalAutoNotice')}
        </Alert>
      );
    }
    return null;
  };

  // フォーム初期データ取得。アンマウント時に公開予約 state をリセットする
  useEffect(() => {
    if (storeId) {
      dispatch(fetchReservationForm(storeId));
    }
    return () => {
      dispatch(resetPublicReservation());
    };
  }, [dispatch, storeId]);

  /**
   * ページタイトル・OGP を店舗前面に動的設定する（form 取得後）。
   * FEAST サービスのブランドを前面に出さず店舗名を主役にする。
   * プレビュー（スタッフ管理画面内）ではブラウザタブを壊さないよう変更しない。
   * アンマウント時に元の値へ戻す（SPA ルート用の index.html デフォルトを保つ）。
   */
  useEffect(() => {
    if (isPreview || !form) return;
    const storeName = form.storeInfo?.storeName?.trim();
    // 既存 meta の書き換え/作成ヘルパー（無ければ作成）
    const setMeta = (selector: string, attr: 'property' | 'name', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return { el, prev };
    };

    const prevTitle = document.title;
    // og:title / twitter:title は常に店舗名ベース（Wave A・2）。店舗名が無い場合も
    // 「ご予約」単独ではなく住所やアクセスなど店舗寄りのフォールバックを使い、無ければ「ご予約」。
    const storeFallback = form.storeInfo?.address?.trim() || form.storeInfo?.accessInfo?.trim();
    const title = storeName
      ? `${storeName} ご予約`
      : storeFallback
        ? `${storeFallback} ご予約`
        : 'ご予約';
    document.title = title;

    // OGP 説明はアクセス情報 or 住所を要約に使う（無ければ据え置きのため設定しない）
    const description = form.storeInfo?.accessInfo?.trim() || form.storeInfo?.address?.trim() || null;
    // og:image はヒーロー画像 → 無ければ店舗ギャラリー先頭 → それも無ければ設定しない
    // （システム/サービス画像で上書きしない）。Wave A・2。
    const image = form.heroImageUrl || form.galleryImageUrls?.[0] || null;

    const restores: { el: HTMLMetaElement; prev: string | null }[] = [];
    restores.push(setMeta('meta[property="og:title"]', 'property', 'og:title', title));
    restores.push(setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title));
    if (description) {
      restores.push(
        setMeta('meta[property="og:description"]', 'property', 'og:description', description),
      );
      restores.push(
        setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description),
      );
    }
    if (image) {
      restores.push(setMeta('meta[property="og:image"]', 'property', 'og:image', image));
      restores.push(setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image));
    }

    return () => {
      document.title = prevTitle;
      // 元の content を復元（作成した meta は元 content が null のはずだが、
      // index.html のデフォルトが常に存在するため content 復元で十分）
      restores.forEach(({ el, prev }) => {
        if (prev !== null) el.setAttribute('content', prev);
      });
    };
  }, [isPreview, form]);

  // 表示月の営業カレンダー取得（1日〜末日）
  useEffect(() => {
    if (!storeId || !form || !accepting) return;
    const pad = (n: number) => `${n}`.padStart(2, '0');
    const lastDay = new Date(viewYear, viewMonth, 0).getDate();
    dispatch(
      fetchPublicBusinessCalendar({
        storeId,
        from: `${viewYear}-${pad(viewMonth)}-01`,
        to: `${viewYear}-${pad(viewMonth)}-${pad(lastDay)}`,
      }),
    );
  }, [dispatch, storeId, form, accepting, viewYear, viewMonth]);

  // 日付選択・人数変更・コース変更時に空き状況を取得（partySize 付きでその人数で入れる枠を返させる）。
  // コース選択時は menuId を渡し、コースの滞在時間で空き判定・受付時間超過判定を行わせる（フィードバックB1）。
  useEffect(() => {
    if (!storeId || !selectedDate) return;
    dispatch(
      fetchPublicAvailability({
        storeId,
        from: selectedDate,
        to: selectedDate,
        partySize,
        menuId: selectedMenuId || undefined,
      }),
    );
  }, [dispatch, storeId, selectedDate, partySize, selectedMenuId]);

  const sortedInquiries = useMemo(
    () => (form ? [...form.inquiries].sort((a, b) => a.sortIndex - b.sortIndex) : []),
    [form],
  );
  const sortedMenus = useMemo(
    () => (form ? [...form.menus].sort((a, b) => a.sortIndex - b.sortIndex) : []),
    [form],
  );

  const calendarDayMap = useMemo(() => {
    const map = new Map<string, BusinessCalendarDay>();
    businessCalendar.forEach((day) => map.set(day.date, day));
    return map;
  }, [businessCalendar]);

  // 月カレンダーのセル（先頭は日曜始まりのオフセット分 null）
  const calendarCells = useMemo(() => {
    const offset = new Date(viewYear, viewMonth - 1, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
    const cells: ({ day: number; dateStr: string; disabled: boolean } | null)[] = [];
    for (let i = 0; i < offset; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateStr = toDateString(new Date(viewYear, viewMonth - 1, day));
      const calDay = calendarDayMap.get(dateStr);
      const beforeToday = dateStr < todayStr;
      // 受付開始オフセット（例「受付開始を1日後」）より前は選べない。
      // acceptanceFromDate 未返却（旧BE）のときは todayStr にフォールバック（従来どおり当日から）。
      const beforeAcceptance = dateStr < (form?.acceptanceFromDate ?? todayStr);
      const afterAcceptance =
        !!form?.acceptanceUntilDate && dateStr > form.acceptanceUntilDate;
      const closed = !!calDay && !calDay.open;
      // 営業日でも「実際に予約可能な時間枠が無い日」（締切超過・満席・在庫なし）は選べないようにする。
      // BEが bookable を返す場合はそれを優先し、未返却（旧BE）のときは従来どおり open で判定する。
      const noBookableSlot = calDay?.bookable === false;
      cells.push({
        day,
        dateStr,
        disabled: beforeToday || beforeAcceptance || afterAcceptance || closed || noBookableSlot,
      });
    }
    return cells;
  }, [viewYear, viewMonth, calendarDayMap, todayStr, form]);

  const isCurrentMonth =
    viewYear === today.getFullYear() && viewMonth === today.getMonth() + 1;

  const moveMonth = (delta: number) => {
    const next = new Date(viewYear, viewMonth - 1 + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth() + 1);
  };

  const handlePartySizeChange = (size: number) => {
    setPartySize(size);
    // 人数が変わると枠の可否も変わるため選択済みの時間はリセットする
    setSelectedSlot(null);
  };

  const handleSelectDate = (dateStr: string) => {
    setSelectedDate(dateStr);
    setSelectedSlot(null);
  };

  // 枠選択。選択したら「日時未選択」エラーを解除する（Wave A・6）
  const handleSelectSlot = (dateTime: string) => {
    setSelectedSlot(dateTime);
    setFieldErrors((prev) => {
      if (!prev.slot) return prev;
      const next = { ...prev };
      delete next.slot;
      return next;
    });
  };

  // コース変更（フィードバックB1）。コースで滞在時間＝空き枠が変わるため選択中の時間はリセットする。
  const handleCourseChange = (menuId: string) => {
    setSelectedMenuId(menuId);
    setSelectedSlot(null);
    // 選択したらコース必須エラーを解除する
    if (menuId) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.course;
        return next;
      });
    }
  };

  // 選択コースの滞在時間（無ければ店舗デフォルト）。dateTime ステップの「ご滞在目安」表示に使う（フィードバックB4）。
  const selectedStayMinutes = useMemo(() => {
    const menu = sortedMenus.find((m) => m.id === selectedMenuId);
    return menu?.stayMinutes ?? form?.defaultStayMinutes ?? null;
  }, [sortedMenus, selectedMenuId, form?.defaultStayMinutes]);

  // 選択営業日の枠。深夜営業（例 17:00〜翌3:00）では BE が「翌日の実時刻」の枠も
  // この営業日に帰属させて返すため、選択日の枠に加えて選択日より後（=翌未明）の枠も含める。
  // availability は選択日ごとに丸ごと入れ替わるため、選択日より前の枠は返らない前提。
  const daySlots = useMemo(
    () =>
      selectedDate
        ? availability.filter((slot) => slot.dateTime.slice(0, 10) >= selectedDate)
        : [],
    [availability, selectedDate],
  );

  /**
   * 枠の時刻ラベル。選択営業日より後の日付（翌未明の深夜枠）は先頭に「翌」を付ける。
   * 例: 選択日 7/5 で dateTime=2026-07-06T01:00 → 「翌1:00」相当（HH:mm 表示は維持）。
   */
  const slotTimeLabel = (slot: { dateTime: string }): string => {
    const time = slot.dateTime.substring(11, 16);
    const isNextDay = !!selectedDate && slot.dateTime.slice(0, 10) > selectedDate;
    return isNextDay ? `${t('nextDayPrefix')}${time}` : time;
  };

  // ---- Step2 バリデーション ----
  /**
   * blur 時の項目単体チェック（メール・電話）。
   * 全角→半角・ハイフン/スペース除去の正規化後に厳格な正規表現で検証する（フィードバック3）。
   */
  const validateFieldOnBlur = (field: 'email' | 'phone') => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (field === 'email') {
        const value = email.trim();
        if (value && !isValidEmail(value)) next.email = t('invalidEmail');
        else delete next.email;
      } else {
        const value = phone.trim();
        if (value && !isValidPhoneNumber(value)) next.phone = t('invalidPhone');
        else delete next.phone;
      }
      return next;
    });
  };

  const validateInfo = (): boolean => {
    const errors: Record<string, string> = {};
    // 日時（枠）は最終的に必須。日時ステップで未選択のまま進めた場合はここで検出する（Wave A・6）。
    if (!selectedSlot) errors.slot = `${t('selectTime')}${t('requiredError')}`;
    if (!userName.trim()) errors.userName = `${t('guestName')}${t('requiredError')}`;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) errors.email = `${t('email')}${t('requiredError')}`;
    else if (!isValidEmail(trimmedEmail)) errors.email = t('invalidEmail');
    const trimmedPhone = phone.trim();
    if (phoneRequired && !trimmedPhone) errors.phone = `${t('phone')}${t('requiredError')}`;
    // 任意入力でも入力がある場合は形式チェックする
    else if (trimmedPhone && !isValidPhoneNumber(trimmedPhone)) errors.phone = t('invalidPhone');
    // コース選択必須（フィードバック4）。未選択はエラー
    if (courseRequired && !selectedMenuId) errors.course = t('courseRequiredError');
    sortedInquiries.forEach((inq) => {
      // BOOLEAN はチェック有無どちらも有効な回答のため必須チェック対象外
      if (inq.required && inq.type !== 'BOOLEAN' && !(answers[inq.id] || '').trim()) {
        errors[inq.id] = `${inq.content}${t('requiredError')}`;
      }
    });
    if (!agreed) errors.agree = t('agreeError');
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInfoNext = () => {
    if (validateInfo()) setActiveStep(stepIndex('confirm'));
  };

  // ---- 送信 ----
  /** 確認済みの入力から予約作成/決済 checkout 共通のボディを組む */
  const buildInput = (slot: string): ReservationInput => {
    const responses = sortedInquiries
      .map((inq) => {
        if (inq.type === 'BOOLEAN') {
          // BOOLEAN はチェック有無を「はい」「いいえ」で送る（回答値は言語によらず固定）
          return { inquiryId: inq.id, content: answers[inq.id] === 'true' ? 'はい' : 'いいえ' };
        }
        const content = (answers[inq.id] || '').trim();
        return content ? { inquiryId: inq.id, content } : null;
      })
      .filter((r): r is { inquiryId: string; content: string } => r !== null);
    // 送信前に正規化（全角→半角・ハイフン/スペース除去）した値を送る（フィードバック3）
    return {
      userName: userName.trim(),
      email: normalizeEmail(email),
      phone: phone.trim() ? normalizePhoneNumber(phone) : null,
      reservationTime: slot,
      numberOfPeople: partySize,
      selectedMenuId: selectedMenuId || null,
      responses,
    };
  };

  /** 3DS リダイレクト復帰時に付ける return_url（照会には遷移せず自ページに戻し finalize する）。 */
  const buildReturnUrl = (reservationId: string): string => {
    const url = new URL(window.location.href);
    // Stripe が付ける payment_intent/redirect_status に加え、どの予約を finalize するか自前で持つ
    url.searchParams.set('checkout_reservation', reservationId);
    return url.toString();
  };

  /**
   * finalize を最大 N 回ポーリングして確定を待つ（3DS 復帰・非3DS 成功共通）。
   * finalized=false / 409（PENDING_PAYMENT）は未完了として少し待って再試行する。
   * 成功で done へ進める。全リトライ尽きたら照会リンク誘導のためのフラグを立てる。
   */
  const runFinalize = async (reservationId: string) => {
    if (!storeId) return;
    setFinalizeExhausted(false);
    setFinalizePolling(true);
    const maxAttempts = 6;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      const action = await dispatch(finalizePublicReservation({ storeId, reservationId }));
      if (finalizePublicReservation.fulfilled.match(action)) {
        const payload = action.payload;
        const done =
          payload.finalized !== false &&
          payload.status !== 'PENDING_PAYMENT' &&
          !!payload.reservationNumber;
        if (done) {
          setFinalizePolling(false);
          setActiveStep(stepIndex('done'));
          return;
        }
      }
      // 未完了 or 409。少し待って再試行（Stripe の webhook/確定反映待ち）。
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    setFinalizePolling(false);
    setFinalizeExhausted(true);
  };

  /** 確認ステップの確定ボタン。決済必須なら checkout→決済ステップ、そうでなければ従来 submit→done。 */
  const handleConfirmPrimary = async () => {
    if (isPreview) {
      setPreviewSnackbar(true);
      return;
    }
    if (!storeId || !selectedSlot) return;
    const input = buildInput(selectedSlot);
    if (paymentRequired) {
      // デポジット・キャンセルポリシーへの同意チェック
      if (!depositAgreed) {
        setFieldErrors((prev) => ({ ...prev, depositAgree: t('depositAgreeError') }));
        return;
      }
      const action = await dispatch(checkoutPublicReservation({ storeId, input }));
      if (checkoutPublicReservation.fulfilled.match(action)) {
        setActiveStep(stepIndex('payment'));
      }
      return;
    }
    const action = await dispatch(submitPublicReservation({ storeId, input }));
    if (submitPublicReservation.fulfilled.match(action)) {
      setActiveStep(stepIndex('done'));
    }
  };

  /** 決済 checkout をやり直す（期限切れ・カード変更）。確認ステップへ戻し clientSecret を破棄する。 */
  const handleRestartPayment = () => {
    dispatch(resetCheckout());
    setFinalizeExhausted(false);
    setFinalizePolling(false);
    redirectHandled.current = false;
    setActiveStep(stepIndex('confirm'));
  };

  /**
   * 3DS リダイレクト復帰の処理（統合契約 B2）。
   * return_url に付けた checkout_reservation（＋Stripe の payment_intent/redirect_status）が
   * ある場合、決済ステップへ移動して finalize をポーリングする。処理後は URL を掃除する。
   */
  useEffect(() => {
    if (isPreview || redirectHandled.current) return;
    const reservationId = searchParams.get('checkout_reservation');
    const redirectStatus = searchParams.get('redirect_status');
    if (!reservationId || !storeId) return;
    redirectHandled.current = true;
    // URL から決済関連クエリを掃除（再読込での二重処理・機微値残留を防ぐ）
    const cleaned = new URLSearchParams(searchParams);
    ['checkout_reservation', 'payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach(
      (k) => cleaned.delete(k),
    );
    setSearchParams(cleaned, { replace: true });
    if (redirectStatus === 'failed') {
      // 3DS 認証失敗。決済ステップに留めて再試行導線を出す（予約は TTL に委ねる）。
      setActiveStep(stepIndex('payment'));
      setFinalizeExhausted(true);
      return;
    }
    setActiveStep(stepIndex('payment'));
    runFinalize(reservationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview, storeId, searchParams, stepIndex]);

  // ---- 描画ヘルパー ----
  const centerSpinner = (
    <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
      <CircularProgress />
    </Box>
  );

  /**
   * コース選択（Select + 選択中コースの画像）。
   * フィードバックB1 で日時ステップの先頭へ移動（コースの滞在時間で空き枠が変わるため日時より前に選ぶ）。
   */
  /** コース1件をカード（ラジオ的）で描画する。value='' は「コースなし」カード。 */
  const renderCourseCard = (menu: PublicMenuView | null) => {
    const id = menu?.id ?? '';
    const selected = selectedMenuId === id;
    const stay = menu?.stayMinutes ?? null;
    const thumb = menu?.imageUrls?.[0];
    return (
      <Paper
        key={id || '__none__'}
        variant="outlined"
        onClick={() => handleCourseChange(id)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.25,
          cursor: 'pointer',
          borderColor: selected ? 'primary.main' : 'divider',
          borderWidth: selected ? 2 : 1,
          bgcolor: selected ? 'action.selected' : 'background.paper',
        }}
      >
        <Radio checked={selected} size="small" sx={{ p: 0.5 }} tabIndex={-1} />
        {menu ? (
          <>
            {thumb && (
              <Box
                component="img"
                src={thumb}
                alt=""
                onClick={(e) => {
                  e.stopPropagation();
                  setViewerImages(menu.imageUrls ?? []);
                }}
                sx={{
                  width: 56,
                  height: 44,
                  objectFit: 'cover',
                  borderRadius: 0.5,
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {menu.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {menuPriceLabel(menu)}
                {stay != null && ` ・ ${t('courseStayLabel')}${stay}${t('courseStaySuffix')}`}
              </Typography>
            </Box>
          </>
        ) : (
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('noCourse')}
            </Typography>
            {form?.defaultStayMinutes != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {`${t('noCourseStayPrefix')}${form.defaultStayMinutes}${t('noCourseStaySuffix')}`}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  /**
   * コース選択（カード/ラジオ的な分かりやすい UI・Wave A・3）。
   * - 見出し＋補足（任意である旨）。
   * - 各コースは名前・税込価格・滞在目安・画像サムネイルをカードで表示。
   * - コース必須でない場合は先頭に「コースなし（席のみ）」カードを置き、店舗デフォルト滞在を明記する。
   */
  const renderCourseSelect = () => {
    if (sortedMenus.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t('courseSectionTitle')}
          {courseRequired && ' *'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {courseRequired ? t('selectCoursePlaceholder') : t('courseSectionOptional')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* コース必須でないときのみ「コースなし」カードを出す */}
          {!courseRequired && renderCourseCard(null)}
          {sortedMenus.map((menu) => renderCourseCard(menu))}
        </Box>
        {fieldErrors.course && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            {fieldErrors.course}
          </FormHelperText>
        )}
      </Box>
    );
  };

  const renderDateTimeStep = () => (
    <Box>
      {/* 承認要否の告知（Wave A・5）。予約トップの目立つ位置に置く。 */}
      {approvalNotice()}

      {/* コース選択を日時より前に置く（フィードバックB1）。コースで滞在時間＝空き枠が変わるため。 */}
      {renderCourseSelect()}

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="party-size-label">{t('selectPartySize')}</InputLabel>
        <Select
          labelId="party-size-label"
          label={t('selectPartySize')}
          value={partySize}
          onChange={(e) => handlePartySizeChange(Number(e.target.value))}
        >
          {partySizes.map((n) => (
            <MenuItem key={n} value={n}>{`${n}${t('people')}`}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* ご滞在目安（フィードバックB4）。選択コースの滞在時間、無ければ店舗デフォルト。 */}
      {selectedStayMinutes != null && (
        <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
          {`${t('stayEstimatePrefix')}${selectedStayMinutes}${t('stayEstimateSuffix')}`}
        </Alert>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t('selectDate')}
      </Typography>
      {form?.acceptanceUntilDate && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`${formatMonthDay(lang, form.acceptanceUntilDate)}${t('acceptanceUntil')}`}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton
          onClick={() => moveMonth(-1)}
          disabled={isCurrentMonth}
          aria-label={t('prevMonth')}
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {formatYearMonth(lang, viewYear, viewMonth)}
        </Typography>
        <IconButton onClick={() => moveMonth(1)} aria-label={t('nextMonth')} size="small">
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {calendarLoading ? (
        centerSpinner
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 2 }}>
          {t('weekdays')
            .split(',')
            .map((weekday) => (
              <Typography
                key={weekday}
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'center' }}
              >
                {weekday}
              </Typography>
            ))}
          {calendarCells.map((cell, index) =>
            cell === null ? (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`empty-${index}`} />
            ) : (
              <Button
                key={cell.dateStr}
                disabled={cell.disabled}
                onClick={() => handleSelectDate(cell.dateStr)}
                variant={selectedDate === cell.dateStr ? 'contained' : 'text'}
                color={selectedDate === cell.dateStr ? 'primary' : 'inherit'}
                sx={{ minWidth: 0, px: 0, py: 1, fontWeight: 500 }}
              >
                {cell.day}
              </Button>
            ),
          )}
        </Box>
      )}

      {selectedDate && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {t('selectTime')}
          </Typography>
          {availabilityLoading ? (
            centerSpinner
          ) : daySlots.length === 0 ? (
            <Alert severity="info">{t('noSlots')}</Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
                gap: 1,
              }}
            >
              {daySlots.map((slot) => {
                const time = slotTimeLabel(slot);
                const showRemaining =
                  slot.bookable &&
                  slot.remainingParties !== INT_MAX &&
                  slot.remainingParties <= REMAINING_BADGE_THRESHOLD;
                // 枠不可の理由で表示を出し分ける（Wave A・4／フィードバック4 B2）。
                // AFTER_HOURS は「満席」表現を使わず「締切」にする。旧BE（reason 未返却）は従来どおり「満席」。
                // チップ内は時刻＋2文字ラベル固定で見切れを防ぎ、フル文言は tooltip/aria-label に回す。
                const afterHours = slot.unavailableReason === 'AFTER_HOURS';
                const shortReason = afterHours ? t('afterHours') : t('full');
                const fullReason = afterHours ? t('afterHoursNote') : t('fullNote');
                const label = !slot.bookable
                  ? `${time} ${shortReason}`
                  : showRemaining
                    ? `${time} ${t('remainingPrefix')}${slot.remainingParties}${t('remainingSuffix')}`
                    : time;
                const selected = selectedSlot === slot.dateTime;
                const chip = (
                  <Chip
                    key={slot.dateTime}
                    label={label}
                    clickable
                    disabled={!slot.bookable}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => handleSelectSlot(slot.dateTime)}
                    aria-label={!slot.bookable ? `${time} ${fullReason}` : undefined}
                  />
                );
                // 不可枠はフル文言をツールチップで補足する（チップ本体は2文字ラベルのまま）。
                return !slot.bookable ? (
                  <Tooltip key={slot.dateTime} title={`${time} ${fullReason}`}>
                    {/* disabled な Chip でも Tooltip が効くよう span でラップ */}
                    <span style={{ display: 'inline-flex' }}>{chip}</span>
                  </Tooltip>
                ) : (
                  chip
                );
              })}
            </Box>
          )}
          {/* AFTER_HOURS の枠がある場合の補足（満席と区別する・Wave A・4） */}
          {!availabilityLoading &&
            daySlots.some((s) => !s.bookable && s.unavailableReason === 'AFTER_HOURS') && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {`「${t('afterHours')}」= ${t('afterHoursNote')}`}
              </Typography>
            )}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          // 時間未選択のときは次で確認できるよう note を出しつつ進める（ハードブロックをやめる・Wave A・6）。
          // コース必須未選択などは info/確認ステップの最終バリデーションで検出する。
          onClick={() => setActiveStep(stepIndex('info'))}
        >
          {t('next')}
        </Button>
      </Box>
    </Box>
  );

  const renderInquiryField = (inquiry: PublicInquiryView) => {
    const value = answers[inquiry.id] || '';
    const error = fieldErrors[inquiry.id];
    const setValue = (content: string) =>
      setAnswers((prev) => ({ ...prev, [inquiry.id]: content }));

    switch (inquiry.type) {
      case 'CHOICE':
        return (
          <FormControl key={inquiry.id} fullWidth size="small" error={!!error} required={inquiry.required}>
            <InputLabel id={`inquiry-${inquiry.id}-label`}>{inquiry.content}</InputLabel>
            <Select
              labelId={`inquiry-${inquiry.id}-label`}
              label={inquiry.content}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            >
              {inquiry.choices.map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {choice}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        );
      case 'BOOLEAN':
        return (
          <FormControlLabel
            key={inquiry.id}
            control={
              <Checkbox
                checked={value === 'true'}
                onChange={(e) => setValue(e.target.checked ? 'true' : 'false')}
              />
            }
            label={inquiry.content}
          />
        );
      case 'NUMBER':
        return (
          <TextField
            key={inquiry.id}
            label={inquiry.content}
            required={inquiry.required}
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={!!error}
            helperText={error || ''}
            fullWidth
            size="small"
          />
        );
      case 'TEXT':
      default:
        return (
          <TextField
            key={inquiry.id}
            label={inquiry.content}
            required={inquiry.required}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={!!error}
            helperText={error || ''}
            fullWidth
            size="small"
            multiline
          />
        );
    }
  };

  const renderInfoStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* 日時/コースは前ステップの選択。未選択で進んだ場合はここで気づけるようにする（Wave A・6） */}
      {(fieldErrors.slot || fieldErrors.course) && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setActiveStep(stepIndex('dateTime'))}>
              {t('back')}
            </Button>
          }
        >
          {fieldErrors.slot || fieldErrors.course}
        </Alert>
      )}
      <TextField
        label={t('guestName')}
        required
        value={userName}
        onChange={(e) => setUserName(e.target.value)}
        error={!!fieldErrors.userName}
        helperText={fieldErrors.userName || ''}
        fullWidth
        size="small"
      />
      <TextField
        label={t('email')}
        required
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => validateFieldOnBlur('email')}
        error={!!fieldErrors.email}
        helperText={fieldErrors.email || t('emailHelp')}
        fullWidth
        size="small"
      />
      <TextField
        label={phoneRequired ? t('phone') : `${t('phone')}${t('optional')}`}
        required={phoneRequired}
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        onBlur={() => validateFieldOnBlur('phone')}
        error={!!fieldErrors.phone}
        helperText={fieldErrors.phone || ''}
        fullWidth
        size="small"
      />

      {/* コース選択は日時ステップの先頭へ移動した（フィードバックB1）。ここでは氏名・連絡先・質問のみ。 */}

      {sortedInquiries.map(renderInquiryField)}

      {/* 同意チェック（ADR 0058）: 予約個人情報は店舗と FEAST の「共同利用」のため、
          店舗の個人情報の取り扱いと FEAST の予約プライバシーポリシーの両方に、1つのチェックで同意する。
          「[店舗名]の[個人情報の取り扱い]、およびFEAST株式会社の[予約プライバシーポリシー]に同意する」。
          店舗リンクは URL 型＝店舗指定の外部 URL、TEXT 型（および privacyPolicy 未返却の旧 BE）＝
          FEAST 公開の /reservations/:store_id/privacy。FEAST リンクは corp の予約プラポリ固定。 */}
      <FormControl error={!!fieldErrors.agree}>
        <FormControlLabel
          control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
          label={
            <span>
              {t('privacyAgreePrefix')}
              {`${form?.storeInfo?.storeName?.trim() || t('privacyStoreFallback')}${t('privacyPossessive')}`}
              <Link
                href={
                  form?.privacyPolicy?.type === 'URL' && form.privacyPolicy.url
                    ? form.privacyPolicy.url
                    : `/reservations/${storeId}/privacy`
                }
                target="_blank"
                rel="noopener"
              >
                {t('privacyPolicy')}
              </Link>
              {t('privacyAgreeConnector')}
              {`${t('privacyFeastName')}${t('privacyPossessive')}`}
              <Link href={FEAST_USER_PRIVACY_URL} target="_blank" rel="noopener">
                {t('privacyFeastPolicy')}
              </Link>
              {t('privacyAgreeSuffix')}
            </span>
          }
        />
        {fieldErrors.agree && <FormHelperText>{fieldErrors.agree}</FormHelperText>}
      </FormControl>

      {/* 匿名化の注記（Wave B）。個人情報入力/同意付近に置き、ユーザー向けプラポリへリンクする。 */}
      <AnonymizationNotice lang={lang} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Button onClick={() => setActiveStep(stepIndex('dateTime'))}>{t('back')}</Button>
        <Button variant="contained" onClick={handleInfoNext}>
          {t('next')}
        </Button>
      </Box>
    </Box>
  );

  /**
   * キャンセルポリシー（返金段階）の表示（Phase 2・統合契約 A7）。
   * cancellationPolicyTiers を hoursBeforeStart 降順に並べ「開始◯時間前まで: △％返金」で列挙する。
   * refundPercent が 0 は「返金なし」、100 は「全額返金」に言い換える。
   */
  const renderCancellationPolicy = (
    tiers: RefundTier[],
    /** 決済額（税込・概算）が分かる場合は各段階に金額換算を併記する */
    baseAmount?: number | null,
    /** 手数料負担ポリシー（顧客都合キャンセル時の挙動の一文を出し分ける） */
    refundFeePolicy?: string,
  ) => {
    if (!tiers || tiers.length === 0) return null;
    const sorted = [...tiers].sort((a, b) => b.hoursBeforeStart - a.hoursBeforeStart);
    const refundLabel = (percent: number): string => {
      if (percent <= 0) return t('refundNoRefund');
      if (percent >= 100) return t('refundFull');
      return `${percent}${t('refundPercentSuffix')}`;
    };
    // 返金額の概算換算（決済額が分かるとき・返金なし段階は付けない）
    const approxLabel = (percent: number): string => {
      if (baseAmount == null || percent <= 0) return '';
      const amount = Math.floor((baseAmount * percent) / 100);
      return `${t('refundApproxPrefix')}${amount.toLocaleString()}${t('refundApproxSuffix')}`;
    };
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t('cancellationPolicyTitle')}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {sorted.map((tier) => (
            <Typography
              key={`${tier.hoursBeforeStart}-${tier.refundPercent}`}
              component="li"
              variant="body2"
              color="text.secondary"
            >
              {`${t('cancellationTierPrefix')}${tier.hoursBeforeStart}${t('cancellationTierRefund')}${refundLabel(tier.refundPercent)}${approxLabel(tier.refundPercent)}`}
            </Typography>
          ))}
        </Box>
        {/* 手数料の扱い（顧客負担/店舗負担）と FEAST の手数料方針（統合契約 A7・feast-be ADR 0103） */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {refundFeePolicy === 'CUSTOMER_BEARS'
            ? t('refundFeeCustomerBears')
            : t('refundFeeStoreAbsorbs')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('feastNoFeeNote')}
        </Typography>
      </Box>
    );
  };

  /** デポジット案内＋キャンセルポリシー＋同意（決済必須店舗の確認ステップに表示） */
  const renderDepositSection = () => {
    if (!paymentRequired) return null;
    const policy = form?.payment;
    const preview = policy?.depositAmountPreview;
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t('depositNoticeTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('depositNoticeBody')}
        </Typography>
        {preview != null && (
          <Box sx={{ mb: 1.5 }}>
            <SummaryRow
              label={t('depositAmountLabel')}
              value={`¥${preview.toLocaleString()}`}
            />
            <Typography variant="caption" color="text.secondary">
              {t('depositAmountPreviewNote')}
            </Typography>
          </Box>
        )}
        {renderCancellationPolicy(
          policy?.cancellationPolicyTiers ?? [],
          preview,
          policy?.refundFeePolicy,
        )}
        <FormControl error={!!fieldErrors.depositAgree}>
          <FormControlLabel
            control={
              <Checkbox
                checked={depositAgreed}
                onChange={(e) => {
                  setDepositAgreed(e.target.checked);
                  if (e.target.checked) {
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.depositAgree;
                      return next;
                    });
                  }
                }}
              />
            }
            label={t('depositAgree')}
          />
          {fieldErrors.depositAgree && (
            <FormHelperText>{fieldErrors.depositAgree}</FormHelperText>
          )}
        </FormControl>
      </Paper>
    );
  };

  const renderConfirmStep = () => {
    const selectedMenu = sortedMenus.find((menu) => menu.id === selectedMenuId) || null;
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('confirmTitle')}
        </Typography>
        {/* 承認要否の告知（Wave A・5）。確認画面の目立つ位置にも出す。 */}
        {approvalNotice()}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {parseApiErrorMessage(submitError, t('submitError'))}
          </Alert>
        )}
        {checkoutError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {parseApiErrorMessage(checkoutError, t('submitError'))}
          </Alert>
        )}
        <Box sx={{ mb: 3 }}>
          {selectedSlot && (
            <SummaryRow label={t('dateTime')} value={formatPublicDateTime(lang, selectedSlot)} />
          )}
          <SummaryRow label={t('partySize')} value={`${partySize}${t('people')}`} />
          <SummaryRow label={t('guestName')} value={userName} />
          <SummaryRow label={t('email')} value={email} />
          {phone.trim() && <SummaryRow label={t('phone')} value={phone} />}
          {sortedMenus.length > 0 && (
            <SummaryRow
              label={t('course')}
              value={
                selectedMenu
                  ? `${selectedMenu.name} ${menuPriceLabel(selectedMenu)}`
                  : t('noCourse')
              }
            />
          )}
          {sortedInquiries.map((inquiry) => {
            const value =
              inquiry.type === 'BOOLEAN'
                ? t(answers[inquiry.id] === 'true' ? 'yes' : 'no')
                : (answers[inquiry.id] || '').trim();
            if (!value) return null;
            return <SummaryRow key={inquiry.id} label={inquiry.content} value={value} />;
          })}
        </Box>
        {/* デポジット案内・キャンセルポリシー・同意（決済必須店舗のみ・Phase 2） */}
        {renderDepositSection()}
        {/* 匿名化の注記（Wave B）。確認画面にも同様に表示する。 */}
        <Box sx={{ mb: 2 }}>
          <AnonymizationNotice lang={lang} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={submitting || checkingOut}
            onClick={() => {
              dispatch(clearSubmitError());
              dispatch(clearCheckoutError());
              setActiveStep(stepIndex('info'));
            }}
          >
            {t('back')}
          </Button>
          <Button
            variant="contained"
            disabled={submitting || checkingOut}
            onClick={handleConfirmPrimary}
          >
            {submitting || checkingOut ? t('submitting') : t('submit')}
          </Button>
        </Box>
      </Box>
    );
  };

  /**
   * 決済ステップ（Phase 2・3DS）。checkout 済みの clientSecret で Stripe Payment Element を表示。
   * finalize ポーリング中・尽きた場合の表示、および 3DS 失敗・期限切れの再試行導線を扱う。
   */
  const renderPaymentStep = () => {
    // finalize 中（3DS 復帰含む）
    if (finalizePolling || finalizing) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">{t('finalizing')}</Typography>
        </Box>
      );
    }
    // finalize が尽きた／3DS 失敗。照会リンク誘導と再試行導線を出す。
    if (finalizeExhausted) {
      return (
        <Box sx={{ py: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {finalizeError ? t('finalizeFailed') : t('finalizePending')}
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            {checkout && (
              <Button variant="contained" onClick={() => runFinalize(checkout.reservationId)}>
                {t('finalizeRetry')}
              </Button>
            )}
            <Button color="inherit" onClick={handleRestartPayment}>
              {t('startOver')}
            </Button>
          </Box>
        </Box>
      );
    }
    // checkout が無い（期限切れ・リロード等）。最初からやり直す導線。
    if (!checkout) {
      return (
        <Box sx={{ py: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('paymentExpired')}
          </Alert>
          <Box sx={{ textAlign: 'center' }}>
            <Button variant="contained" onClick={handleRestartPayment}>
              {t('startOver')}
            </Button>
          </Box>
        </Box>
      );
    }
    return (
      <ReservationPaymentStep
        lang={lang}
        checkout={checkout}
        returnUrl={buildReturnUrl(checkout.reservationId)}
        onConfirmed={() => runFinalize(checkout.reservationId)}
        onBack={handleRestartPayment}
      />
    );
  };

  /**
   * 店舗名＋住所＋連絡先の案内ブロック（B7）。予約完了・照会で店舗と連絡手段が分かるようにする。
   * 住所は Google マップリンク（既存導線を踏襲）。設定済みの項目のみ表示する。
   */
  const renderStoreGuide = () => {
    const info = form?.storeInfo;
    const storeName = info?.storeName?.trim();
    const address = info?.address?.trim();
    const contact = info?.contactInfo?.trim();
    if (!storeName && !address && !contact) return null;
    const guideRow = (label: string, node: React.ReactNode) => (
      <Box sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} component="div">
          {node}
        </Typography>
      </Box>
    );
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 3, textAlign: 'left' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {t('storeGuideTitle')}
        </Typography>
        {storeName && guideRow(t('storeNameLabel'), storeName)}
        {address &&
          guideRow(
            t('addressLabel'),
            <Link href={googleMapsUrl(address)} target="_blank" rel="noopener">
              {address}
            </Link>,
          )}
        {contact && guideRow(t('storeContactLabel'), contact)}
      </Paper>
    );
  };

  const renderDoneStep = () => {
    // 決済フロー（Phase 2）では finalize 応答を、従来フローでは submit 応答を使う。
    const reservationNumber = result?.reservationNumber ?? finalizeResult?.reservationNumber;
    const status = result?.status ?? finalizeResult?.status;
    const managementLink = finalizeResult?.managementLink;
    if (!reservationNumber) return null;
    const auto = status === 'CONFIRMED';
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          {auto ? t('doneTitleAuto') : t('doneTitleManual')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {auto ? t('doneBodyAuto') : t('doneBodyManual')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('reservationNumber')}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 2, mb: 1 }}>
          {reservationNumber}
        </Typography>
        {/* デポジット決済で確定した場合の支払い済み表示 */}
        {checkout && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('depositPaidLabel')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`¥${checkout.amount.toLocaleString()}`}
            </Typography>
          </Box>
        )}
        {/* 予約日時と、確定した来店予定時刻である旨の注記（B7） */}
        {selectedSlot && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('dateTime')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatPublicDateTime(lang, selectedSlot)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('visitDateTimeNote')}
            </Typography>
          </Box>
        )}
        <Alert severity="info" sx={{ textAlign: 'left' }}>
          {t('manageHint')}
        </Alert>
        {/* finalize が返す照会リンク（決済フロー）。予約内容の確認・キャンセルへ導線 */}
        {managementLink && (
          <Button
            variant="outlined"
            href={managementLink}
            sx={{ mt: 2 }}
            target="_blank"
            rel="noopener"
          >
            {t('manageLinkButton')}
          </Button>
        )}
        {/* 店舗名・住所・連絡先の案内（B7） */}
        {renderStoreGuide()}
      </Box>
    );
  };

  /**
   * 店舗情報セクション（契約E）。設定済みの項目のみ表示し、
   * 住所は Google マップリンク・電話は tel: リンクにする。末尾にギャラリーを表示する。
   * 全項目未設定かつギャラリーも空なら何も描画しない。
   */
  const renderStoreInfoSection = () => {
    const info = form?.storeInfo;
    const gallery = form?.galleryImageUrls ?? [];
    const rows: { key: PublicReservationDictKey; value: string; href?: string }[] = [];
    const push = (key: PublicReservationDictKey, value?: string | null, href?: string) => {
      if (value && value.trim()) rows.push({ key, value: value.trim(), href });
    };
    if (info) {
      const address = info.address?.trim();
      push('addressLabel', info.address, address ? googleMapsUrl(address) : undefined);
      push('accessLabel', info.accessInfo);
      push('phone', info.phone, info.phone ? `tel:${info.phone.replace(/[^\d+]/g, '')}` : undefined);
      push('hoursLabel', info.businessHoursNote);
      push('closedDaysLabel', info.closedDaysNote);
      push('paymentLabel', info.paymentMethodsNote);
      push('serviceChargeLabel', info.serviceChargeNote);
      push('cancellationLabel', info.cancellationPolicyNote);
      push('dressCodeLabel', info.dressCodeNote);
      push('seatsLabel', info.seatsNote);
      push('smokingLabel', info.smokingNote);
      push('childrenLabel', info.childrenNote);
      push('petLabel', info.petNote);
      push('parkingLabel', info.parkingNote);
      push('storeNotesLabel', info.storeNotes);
    }
    if (rows.length === 0 && gallery.length === 0) return null;
    return (
      <Accordion defaultExpanded disableGutters sx={{ mt: 4 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('storeInfoTitle')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {rows.map((row) => (
            <Box
              key={row.key}
              sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t(row.key)}
              </Typography>
              {row.href ? (
                <Typography variant="body2">
                  <Link
                    href={row.href}
                    {...(row.href.startsWith('tel:')
                      ? {}
                      : { target: '_blank', rel: 'noopener' })}
                  >
                    {row.value}
                  </Link>
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {row.value}
                </Typography>
              )}
            </Box>
          ))}
          {gallery.length > 0 && (
            <Box sx={{ mt: rows.length > 0 ? 2 : 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('photos')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                {gallery.map((url) => (
                  <Box
                    key={url}
                    component="img"
                    src={url}
                    alt={t('photos')}
                    onClick={() => setViewerImages(gallery)}
                    sx={{
                      width: '100%',
                      aspectRatio: '4 / 3',
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      display: 'block',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const currentStepKey = stepDefs[activeStep]?.key;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PublicHeader storeName={form?.storeInfo?.storeName?.trim() || undefined} />
      <Container maxWidth="sm" sx={{ mt: 3, mb: 6, flexGrow: 1, width: '100%' }}>
        {/* ヒーロー画像（未設定なら非表示・契約E） */}
        {form?.heroImageUrl && (
          <Box
            component="img"
            src={form.heroImageUrl}
            alt={form.storeInfo?.storeName ?? ''}
            sx={{
              width: '100%',
              maxHeight: 240,
              objectFit: 'cover',
              borderRadius: 1,
              display: 'block',
              mb: 2,
            }}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: isPreview ? 'space-between' : 'flex-end',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1,
          }}
        >
          {isPreview && (
            <Chip
              icon={<VisibilityOutlinedIcon />}
              label={t('previewBanner')}
              color="primary"
              variant="outlined"
            />
          )}
          <LanguageToggle lang={lang} onChange={setLang} />
        </Box>

        {/* 店舗名をページトップに表示（未取得・空なら非表示） */}
        {form?.storeInfo?.storeName?.trim() && (
          <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700, mb: 0.5 }}>
            {form.storeInfo.storeName}
          </Typography>
        )}
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 2 }}>
          {t('reservationHeading')}
        </Typography>

        {formLoading ? (
          centerSpinner
        ) : formError ? (
          <Alert severity="error">{t('fetchError')}</Alert>
        ) : form && !accepting ? (
          <Typography variant="h5" align="center" sx={{ my: 10, fontWeight: 600 }}>
            {t('notAccepting')}
          </Typography>
        ) : form ? (
          <>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {stepDefs.map((step) => (
                <Step key={step.key}>
                  <StepLabel>{t(step.labelKey)}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {currentStepKey === 'dateTime' && renderDateTimeStep()}
            {currentStepKey === 'info' && renderInfoStep()}
            {currentStepKey === 'confirm' && renderConfirmStep()}
            {currentStepKey === 'payment' && renderPaymentStep()}
            {currentStepKey === 'done' && renderDoneStep()}
            {/* 店舗情報+ギャラリーは最初のステップでのみ表示する（入力・確認画面を圧迫しない） */}
            {currentStepKey === 'dateTime' && renderStoreInfoSection()}
          </>
        ) : null}

        {/* サービス提供者（FEAST株式会社）の明示とユーザー情報の取り扱いリンク（フィードバック3） */}
        <PublicReservationFooter lang={lang} />
      </Container>

      {/* 画像ビューア（コース画像・ギャラリーの全枚数表示） */}
      <Dialog
        open={viewerImages !== null}
        onClose={() => setViewerImages(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(viewerImages ?? []).map((url) => (
              <Box
                key={url}
                component="img"
                src={url}
                alt=""
                sx={{ width: '100%', borderRadius: 1, display: 'block' }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setViewerImages(null)}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={previewSnackbar}
        autoHideDuration={2500}
        onClose={() => setPreviewSnackbar(false)}
        message={t('previewNote')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <PublicFooter refSource="reservation" />
    </Box>
  );
};

export default PublicReservation;
